/**
 * Add people to a project: search the organization, then Add. Owners and
 * Contributors can work in a project, and so can the brand's own guests; a
 * Viewer, or a guest of another brand, cannot. Shared by Share this project
 * and by Share this chat when nobody else is in the project yet. With nobody
 * in the organization left to add, it points to Settings, where people are
 * invited.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Search, Settings } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import { UserMark } from "@/app/routes/dashboard/components/UserMark";
import { useOrganizationMembers } from "@/app/routes/dashboard/settings/api/useOrganization";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useWorkshopMembers } from "../api/useWorkshopMembers";

type Props = {
  projectId: string;
  /** Read only while the dialog around it is open. */
  isOpen: boolean;
  /** "Add people", "Add to the project". */
  heading: string;
  headingId: string;
};

export default function ProjectAddPeople({ projectId, isOpen, heading, headingId }: Props) {
  const organizationId = useWorkspaceStore((s) => s.selectedOrganizationId);
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { owner, members, addMembers } = useWorkshopMembers(isOpen ? projectId : null);
  const orgMembers = useOrganizationMembers(isOpen ? organizationId : null);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();
  const orgName = useSelectedOrganization().selectedOrganization?.organization.name ?? null;

  // Who can work in a project at all: editors, and the brand's own guests
  // (clients granted this brand). The owner is in already.
  const eligible = useMemo(
    () =>
      (orgMembers.data ?? []).filter(
        (m) =>
          m.userId !== owner?.userId &&
          (m.role === "Owner" ||
            m.role === "Contributor" ||
            (m.role === "Guest" && !!brandId && (m.brands ?? []).some((b) => b.id === brandId))),
      ),
    [orgMembers.data, owner?.userId, brandId],
  );
  const candidates = useMemo(() => {
    const inProject = new Set(members.map((m) => m.userId));
    const q = filter.trim().toLowerCase();
    return eligible
      .filter((m) => !inProject.has(m.userId))
      .filter((m) => !q || m.name?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [eligible, members, filter]);
  const nobodyAtAll = !orgMembers.isLoading && eligible.length === 0;

  if (nobodyAtAll) {
    return (
      <div className="ws-share-nobody">
        <p className="ws-share-note">
          Nobody else is on {orgName ?? "your team"} yet. Invite people in Settings, then add them here.
        </p>
        <button type="button" className="ws-share-link" onClick={() => navigate("/dashboard/settings?page=people")}>
          <Settings aria-hidden="true" />
          Open Settings
        </button>
      </div>
    );
  }

  return (
    <section className="ws-share-group" aria-labelledby={headingId}>
      <h3 id={headingId} className="ws-share-label">
        {heading}
      </h3>
      <label className="ws-share-search">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Search the ${orgName ?? "organization"} team`}
          aria-label={`Search the ${orgName ?? "organization"} team`}
        />
      </label>
      <div className="ws-share-candidates">
        {orgMembers.isLoading ? (
          <div className="flex flex-col gap-2 py-1.5" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-2">
                <span className="size-8 shrink-0 animate-pulse rounded-full bg-co-hover" />
                <span className="h-3 w-40 animate-pulse rounded-full bg-co-hover" />
              </div>
            ))}
          </div>
        ) : candidates.length === 0 ? (
          <p className="ws-share-note">
            {filter.trim()
              ? "Nobody matches."
              : "Everyone who can edit is already in. Viewers, and guests of other brands, can’t be added."}
          </p>
        ) : (
          candidates.map((m) => (
            <div key={m.userId} className="ws-person">
              <UserMark name={m.name || m.email} size={32} />
              <div className="min-w-0 flex-1">
                <div className="ws-person-name">{m.name || m.email}</div>
                <div className="ws-person-detail">
                  {m.role === "Guest" ? "A client of this brand" : orgName ? `On the ${orgName} team` : m.email}
                </div>
              </div>
              <KitButton
                type="button"
                variant="secondary"
                size="sm"
                aria-label={`Add ${m.name || m.email} to the project`}
                onClick={() => addMembers.mutate([{ userId: m.userId, name: m.name || null, email: m.email }])}
              >
                Add
              </KitButton>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
